import type { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import type { ShownInSplitView, SplitViewSelection } from './settingEditor.types'
import { useSettingEditorMode } from './useSettingEditorMode'
import { useSettingEditorSelection } from './useSettingEditorSelection'
import { useSplitViewHighlight } from './useSplitViewHighlight'

type SettingEditorParams = {
  activeSystem: System | null
  isCollectedSettings: boolean
  configurableProperties: ConfigurableProperty[]
}

type SettingEditor = ReturnType<typeof useSettingEditorSelection> & {
  splitViewAvailable: boolean
  opensInSplitView: boolean
  /** Moves the open editor between the popup and the split view, and keeps the choice for next time. */
  setOpensInSplitView: (opensInSplitView: boolean) => void
  /** What the split view is open for; null while it is closed or the editor is in the popup. */
  splitViewSelection: SplitViewSelection | null
  /** What the table and calendar highlight. */
  highlighted: SplitViewSelection | null
  /** Give to the split view, which says what it shows as the user moves around in it. */
  onShownChange: (shownInSplitView: ShownInSplitView) => void
}

/**
 * The setting editor the view has open, in the popup or in the split view. The split view moves on from
 * what opened it: to the setting a save stored, under the new id the server gives every update, or to a
 * setting opened from its own list. Moving the editor to the popup opens what the split view shows, not
 * what the user first clicked, which may no longer exist.
 */
export function useSettingEditor({
  activeSystem,
  isCollectedSettings,
  configurableProperties,
}: SettingEditorParams): SettingEditor {
  const mode = useSettingEditorMode({ activeSystem, isCollectedSettings })
  const editorSelection = useSettingEditorSelection(configurableProperties)
  const { selection, revision, selectProperty, selectSetting } = editorSelection
  const splitViewSelection =
    mode.opensInSplitView && (selection?.kind === 'property' || selection?.kind === 'setting') ? selection : null
  const { shown, highlighted, onShownChange } = useSplitViewHighlight(splitViewSelection, revision)

  const setOpensInSplitView = (opensInSplitView: boolean): void => {
    if (!opensInSplitView && shown) {
      if (shown.kind === 'setting') {
        selectSetting(shown.setting)
      } else {
        selectProperty(shown.property)
      }
    }
    mode.setOpensInSplitView(opensInSplitView)
  }

  return {
    ...editorSelection,
    splitViewAvailable: mode.splitViewAvailable,
    opensInSplitView: mode.opensInSplitView,
    setOpensInSplitView,
    splitViewSelection,
    highlighted,
    onShownChange,
  }
}
